import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { useTheme } from "@mui/material/styles";

// ** Third-Party Imports
import { toast, ToastContainer } from "react-toastify";

// ** Logic Imports
import { GetAssets } from "screens/Inventory/logic/AssetService";
import { GetMaterials } from "screens/Inventory/logic/MaterialService";
import { GetTypeCatalog } from "screens/Inventory/logic/TypeCatalogService";
import { GetSkills } from "screens/Employees/logic/GetSkills";
import { GetEmployees } from "screens/Employees/logic/GetEmployees";
import { GetSubcontractors } from "screens/Assessments/logic/GetSubcontractors";
import { getUnits } from "screens/Inventory/logic/UnitofMeasurementlogic"; 

import {
  GetDefaultAssets,
  SaveDefaultAsset,
  DeleteDefaultAsset,
} from "./DefaultAsset";
import {
  GetDefaultMaterials,
  SaveDefaultMaterial,
  DeleteDefaultMaterial,
} from "./DefaultMaterial";
import {
  GetDefaultLabors,
  SaveDefaultLabor,
  DeleteDefaultLabor,
} from "./DefaultLabor";
import {
  GetDefaultMiscCosts,
  SaveDefaultMiscCost,
  DeleteDefaultMiscCost,
} from "./DefaultMiscCost";
import { DeleteDefaultSubcontractor, GetDefaultSubcontractors, SaveDefaultSubcontractor } from "./DefaultSubcontractor";

export const useDefaultResourceLogic = () => {
  //#region Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  //#endregion

  //#region States
  const [allMaterials, setAllMaterials] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [skillTypes, setSkillTypes] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [defaultAssets, setDefaultAssets] = useState([]);
  const [defaultMaterials, setDefaultMaterials] = useState([]);
  const [defaultLabors, setDefaultLabors] = useState([]);
  const [defaultMiscCosts, setDefaultMiscCosts] = useState([]);
  const [defaultSubcontractors, setDefaultSubcontractors] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [units, setUnits] = useState([]);
  //#endregion States

 
  useEffect(() => {
    fetchMaterials();
    fetchMaterialTypes();
    fetchAssets();
    fetchAssetTypes();
    fetchSkillTypes();
    fetchEmployees();
    fetchSubcontractors();
    fetchUnitsData();
  }, []);

  const fetchSubcontractors = async () => {
    try {
      const data = await GetSubcontractors(0, navigate);
      if(!data.isError){
        setSubcontractors(data);
      }
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
    }
  };
  
  const fetchUnitsData = async () => {
    try {
      const unitsResponse = await getUnits();
      if (!unitsResponse.isError && unitsResponse.result) {
        const formattedUnits = unitsResponse.result.map(unit => {
          const symbol = unit.unitSymbol || ''; 
          const name = unit.unitName || '';
          
          let label = '';
          
          label = `${name}${symbol ? ` (${symbol})` : ''}`

          return {
            value: unit.unitID,
            label: label,
            originalData: unit
          };
        });
        setUnits(formattedUnits);
      } else {
        console.error("Failed to load units:", unitsResponse.message);
        // toast.error("Failed to load units", {
        //   style: {
        //     fontFamily: '"DM Sans", sans-serif',
        //     fontSize: '15px',
        //   },
        // });
      }
    } catch (error) {
      console.error("Error fetching units:", error);
      toast.error("Error loading units", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  };

  const handleUnitCreated = async (newUnit) => {
    await fetchUnitsData();
    if (newUnit && newUnit.result) {
      const createdUnit = newUnit.result;
      const unitValue = createdUnit.unitID;
    }
  };

  const fetchDefaultResources = async (serviceOfferingID) => {
    try {
      const [
        assetsResponse,
        materialsResponse,
        laborsResponse,
        subcontractorsResponse,
        miscCostsResponse,
      ] = await Promise.all([
        GetDefaultAssets(serviceOfferingID, navigate),
        GetDefaultMaterials(serviceOfferingID, navigate),
        GetDefaultLabors(serviceOfferingID, navigate),
        GetDefaultSubcontractors(serviceOfferingID, navigate),
        GetDefaultMiscCosts(serviceOfferingID, navigate),
      ]);

      if (assetsResponse && !assetsResponse.isError) {
        const assetsWithId = (assetsResponse.result || []).map((asset) => ({
          ...asset,
          id: asset.defaultJobAssetID,
        }));
        setDefaultAssets(assetsWithId);
      } else {
        console.error(
          "Failed to fetch default assets:",
          assetsResponse?.message
        );
        setDefaultAssets([]);
      }

      if (materialsResponse && !materialsResponse.isError) {
        const materialsWithId = (materialsResponse.result || []).map(
          (material) => ({
            ...material,
            id: material.defaultJobMaterialID,
          })
        );
        setDefaultMaterials(materialsWithId);
      } else {
        console.error(
          "Failed to fetch default materials:",
          materialsResponse?.message
        );
        setDefaultMaterials([]);
      }

      if (laborsResponse && !laborsResponse.isError) {
        const laborsWithId = (laborsResponse.result || []).map((labor) => ({
          ...labor,
          id: labor.defaultJobLaborID,
        }));
        setDefaultLabors(laborsWithId);
      } else {
        console.error(
          "Failed to fetch default labors:",
          laborsResponse?.message
        );
        setDefaultLabors([]);
      }

      if (subcontractorsResponse && !subcontractorsResponse.isError) {
        const subcontractorsWithId = (subcontractorsResponse.result || []).map((subcontractor) => ({
          ...subcontractor,
          id: subcontractor.defaultJobSubcontractorID,
        }));
        setDefaultSubcontractors(subcontractorsWithId);
      } else {
        console.error(
          "Failed to fetch default subcontractors:",
          subcontractorsResponse?.message
        );
        setDefaultSubcontractors([]);
      }

      if (miscCostsResponse && !miscCostsResponse.isError) {
        const miscCostsWithId = (miscCostsResponse.result || []).map(
          (miscCost) => ({
            ...miscCost,
            id: miscCost.defaultJobMiscCostID,
          })
        );
        setDefaultMiscCosts(miscCostsWithId);
      } else {
        console.error(
          "Failed to fetch default other costs:",
          miscCostsResponse?.message
        );
        setDefaultMiscCosts([]);
      }
    } catch (error) {
      console.error("Error fetching default resources:", error);

      setDefaultAssets([]);
      setDefaultMaterials([]);
      setDefaultLabors([]);
      setDefaultSubcontractors([]);
      setDefaultMiscCosts([]);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await GetMaterials();

      if (!response.isError) {
        setAllMaterials(response.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await GetTypeCatalog("Material");

      if (!response.isError) {
        setMaterialTypes(response.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await GetAssets();

      if (!response.isError) {
        setAllAssets(response.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const response = await GetTypeCatalog("Asset");

      if (!response.isError) {
        setAssetTypes(response.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSkillTypes = async () => {
    try {
      const response = await GetSkills(0, navigate);

      if (response) {
        setSkillTypes(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await GetEmployees(0, navigate);

      if (response) {
        setAllEmployees(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveDefaultResources = async (
    assets,
    materials,
    labors,
    subcontractors,
    otherCosts,
    serviceOfferingID
  ) => {
    if (assets && assets.length > 0) {
      try {
        const validAssets = assets.filter(asset => 
          asset.assetID && asset.assetTypeID
        );

        const responses = await Promise.all(
          validAssets.map((asset) => 
              SaveDefaultAsset(serviceOfferingID, asset, navigate)
          )
        );

        const hasFailure = responses.some(
          (response) => response === false || response?.isError || !response
        );

        if (hasFailure) {
          toast.error("Failed to save default assets.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } catch (error) {
        console.error("Error saving default assets:", error);
        toast.error("Failed to save default assets.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    }

    if (materials && materials.length > 0) {
      try {
        const validMaterials = materials.filter(material => 
          material.materialID && material.materialTypeID
        );

        const responses = await Promise.all(
          validMaterials.map((material) =>
            SaveDefaultMaterial(serviceOfferingID, material, navigate)
          )
        );

        const hasFailure = responses.some(
          (response) => response === false || response?.isError || !response
        );

        if (hasFailure) {
          toast.error("Failed to save default materials.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } catch (error) {
        console.error("Error saving default materials:", error);
        toast.error("Failed to save default materials.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    }

    if (labors && labors.length > 0) {
      try {
        const validLabors = labors.filter(labor => 
          labor.employeeID && labor.skillID
        );

        const responses = await Promise.all(
          validLabors.map((labor) =>
            SaveDefaultLabor(serviceOfferingID, labor, navigate)
          )
        );

        const hasFailure = responses.some(
          (response) => response === false || response?.isError || !response
        );

        if (hasFailure) {
          toast.error("Failed to save default labors.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } catch (error) {
        console.error("Error saving default labors:", error);
        toast.error("Failed to save default labors.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    }

    if (subcontractors && subcontractors.length > 0) {
      try {
        const validSubcontractors = subcontractors.filter(subcontractor => 
          subcontractor.employeeID && subcontractor.skillID
        );

        const responses = await Promise.all(
          validSubcontractors.map((subcontractor) =>
            SaveDefaultSubcontractor(serviceOfferingID, subcontractor, navigate)
          )
        );

        const hasFailure = responses.some(
          (response) => response === false || response?.isError || !response
        );

        if (hasFailure) {
          toast.error("Failed to save default subcontractors.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } catch (error) {
        console.error("Error saving default subcontractors:", error);
        toast.error("Failed to save default subcontractors.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    }

    if (otherCosts && otherCosts.length > 0) {
      try {
        const validOtherCosts = otherCosts.filter(otherCost => 
          otherCost.costName && otherCost.costName !== ""
        );

        const responses = await Promise.all(
          validOtherCosts.map((otherCost) =>
            SaveDefaultMiscCost(serviceOfferingID, otherCost, navigate)
          )
        );

        const hasFailure = responses.some(
          (response) => response === false || response?.isError || !response
        );

        if (hasFailure) {
          toast.error("Failed to save default other costs.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      } catch (error) {
        console.error("Error saving default other costs:", error);
        toast.error("Failed to save default other costs.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    }
    return true;
  };

  const handleDeleteDefaultMaterial = async (defaultJobMaterialID) => {
    try {
      const response = await DeleteDefaultMaterial(
        defaultJobMaterialID,
        navigate
      );

      if (!response.isError) {
        toast.success("Resource has been deleted successfully.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchDefaultResources();
      } else {
        toast.error("Failed to delete resource.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleDeleteDefaultAsset = async (defaultJobAssetID) => {
    try {
      const response = await DeleteDefaultAsset(defaultJobAssetID, navigate);

      if (!response.isError) {
        toast.success("Resource has been deleted successfully.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchDefaultResources();
      } else {
        toast.error("Failed to delete resource.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteDefaultLabor = async (defaultJobLaborID) => {
    try {
      const response = await DeleteDefaultLabor(defaultJobLaborID, navigate);

      if (!response.isError) {
        toast.success("Resource has been deleted successfully.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchDefaultResources();
      } else {
        toast.error("Failed to delete resource.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteDefaultSubcontractor = async (defaultJobSubcontractorID) => {
    try {
      const response = await DeleteDefaultSubcontractor(defaultJobSubcontractorID, navigate);

      if (!response.isError) {
        toast.success("Resource has been deleted successfully.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchDefaultResources();
      } else {
        toast.error("Failed to delete resource.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteDefaultMiscCost = async (defaultJobMiscCostID) => {
    try {
      const response = await DeleteDefaultMiscCost(
        defaultJobMiscCostID,
        navigate
      );

      if (!response.isError) {
        toast.success("Resource has been deleted successfully.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        fetchDefaultResources();
      } else {
        toast.error("Failed to delete resource.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetDefaultResources = () => {
    setDefaultAssets([]);
    setDefaultMaterials([]);
    setDefaultLabors([]);
    setDefaultSubcontractors([]);
    setDefaultMiscCosts([]);
  };
  //#endregion handlers and methods

  //#region Props
  const DefaultResourceProps = {
    allMaterials,
    materialTypes,
    allAssets,
    assetTypes,
    skillTypes,
    allEmployees,
    subcontractors,
    fetchDefaultResources,
    defaultAssets,
    setDefaultAssets,
    defaultMaterials,
    setDefaultMaterials,
    defaultLabors,
    setDefaultLabors,
    defaultSubcontractors,
    setDefaultSubcontractors,
    defaultMiscCosts,
    setDefaultMiscCosts,
    resetDefaultResources,
    handleSaveDefaultResources,
    handleDeleteDefaultMaterial,
    handleDeleteDefaultAsset,
    handleDeleteDefaultLabor,
    handleDeleteDefaultSubcontractor,
    handleDeleteDefaultMiscCost,
    units,
    fetchUnitsData,
    handleUnitCreated,
  };
  //#endregion end of IndexProps

  return {
    DefaultResourceProps,
  };
};